import { parseISO } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SearchBar } from "src/components/atoms/SearchBar";
import { ToastProvider, useModal, useToastMessage, useViewport } from "src/contexts";

import { configurationsLandingPath, createCommunitiesUrl } from "src/RouteUrls";
import { Button, Checkbox, Icon, Modal, ToastMessageProps, TooltipV2 } from "~components";
import { Css } from "~generated/css";
import {
  CheckoutConfigsOrder,
  CheckoutConfigStatus,
  DateOperation,
  HomeownersOrder,
  Order,
} from "~generated/graphqlTypes";
import {
  DATE_WITH_TIME_FORMAT,
  dateFormatter,
  DateOnly,
  defaultTestId,
  phoneMask,
  queryResult,
  SortState,
  SortStateKey,
  useSortState,
  useTestIds,
} from "~utils";
import { AddAuthorizedUserModal } from "./components/AddAuthorizedUserModal";
import {
  InternalConfigurationsLandingPage_CheckoutConfigFragment,
  InternalConfigurationsLandingPage_HomeownerFragment,
  useInternalConfigurationsLandingPage_ArchiveCheckoutConfigMutation,
  useInternalConfigurationsLandingPage_AuthorizedUsersQuery,
  useInternalConfigurationsLandingPage_SaveHomeownerMutation,
  useInternalConfigurationsLandingPage_UnReserveCheckoutConfigMutation,
  useInternalConfigurationsLandingPageQuery,
} from "./InternalConfigurationsLandingPage.generated";

type LandingPageTabType = CheckoutConfigStatus | "AUTHORIZED_USERS" | "INTERNAL";
type CheckoutConfigsSortKey = SortStateKey<CheckoutConfigsOrder>;

// Desktop uses flexible fr units to fill available space
// Mobile and tablet use fixed pixel widths to enable horizontal scrolling and prevent content squishing
const MOBILE_TABLE_WIDTH = 1320; // Sum of mobile column widths: 280 + 300 + 180 + 180 + 200 + 180
const TABLE_GRID_STYLES = Css.dg
  .gtc("1.5fr 1fr 1.5fr 1fr 1fr 1fr 100px")
  .gap0.ifMobileOrTablet.gtc("300px 250px 300px 180px 200px 180px").$;

type InternalConfigurationsLandingPageProps = {
  handleSignOut: () => Promise<void>;
};

export function InternalConfigurationsLandingPage(props: InternalConfigurationsLandingPageProps) {
  return (
    <ToastProvider>
      <InternalConfigurationsLandingPageContent {...props} />
    </ToastProvider>
  );
}

function InternalConfigurationsLandingPageContent({ handleSignOut }: InternalConfigurationsLandingPageProps) {
  const [activeTab, setActiveTab] = useState<LandingPageTabType>(CheckoutConfigStatus.Draft);
  const [search, setSearch] = useState<string>("");
  const { showToast } = useToastMessage();
  const {
    sortState: checkoutConfigsSortState,
    toggleSort: toggleCheckoutConfigsSort,
    order: checkoutConfigsOrder,
  } = useSortState<CheckoutConfigsOrder>("checkoutConfigsList", "createdAt", Order.Desc);

  const isAuthorizedUsersTab = activeTab === "AUTHORIZED_USERS";
  const isInternalTab = activeTab === "INTERNAL";

  // There is a user group called "Checkout Team Testers" that contains the internal tech team users in Blueprint
  // We use this list to filter the internal tech team configs, so the sales team can easily access homeowner configs

  const query = useInternalConfigurationsLandingPageQuery({
    variables: {
      filter: !isAuthorizedUsersTab
        ? isInternalTab
          ? { search, isTechTeam: true }
          : { status: [activeTab], search, isTechTeam: false }
        : undefined,
      page: { limit: 50 },
      order: checkoutConfigsOrder,
    },
    skip: isAuthorizedUsersTab,
  });
  const hasNextPage = query.data?.checkoutConfigs.pageInfo.hasNextPage ?? false;

  const maybeFetchMore = useCallback(async () => {
    if (hasNextPage && !isAuthorizedUsersTab) {
      await query.fetchMore({
        variables: {
          page: { offset: query.data?.checkoutConfigs.entities?.length, limit: 50 },
        },
      });
    }
  }, [hasNextPage, isAuthorizedUsersTab, query]);

  return queryResult(query, ({ checkoutConfigs }) => (
    <InternalConfigurationsLandingPageView
      configurations={checkoutConfigs?.entities}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      setSearch={setSearch}
      search={search}
      maybeFetchMore={maybeFetchMore}
      hasNextPage={hasNextPage}
      isAuthorizedUsersTab={isAuthorizedUsersTab}
      showToast={showToast}
      handleSignOut={handleSignOut}
      checkoutConfigsSortState={checkoutConfigsSortState}
      toggleCheckoutConfigsSort={toggleCheckoutConfigsSort}
    />
  ));
}

type InternalConfigurationsLandingPageViewProps = {
  configurations: InternalConfigurationsLandingPage_CheckoutConfigFragment[];
  activeTab: LandingPageTabType;
  setActiveTab: (tab: LandingPageTabType) => void;
  setSearch: (search: string) => void;
  search: string;
  maybeFetchMore?: () => Promise<void>;
  hasNextPage: boolean;
  isAuthorizedUsersTab: boolean;
  showToast: (toast: ToastMessageProps) => void;
  handleSignOut: () => Promise<void>;
  checkoutConfigsSortState: SortState<CheckoutConfigsSortKey>;
  toggleCheckoutConfigsSort: (key: CheckoutConfigsSortKey) => void;
};

export function InternalConfigurationsLandingPageView({
  configurations,
  activeTab,
  setActiveTab,
  setSearch,
  search,
  maybeFetchMore,
  hasNextPage,
  isAuthorizedUsersTab,
  showToast,
  handleSignOut,
  checkoutConfigsSortState,
  toggleCheckoutConfigsSort,
}: InternalConfigurationsLandingPageViewProps) {
  const tabs: { status: LandingPageTabType; label: string }[] = [
    { status: CheckoutConfigStatus.Draft, label: "In Progress" },
    { status: CheckoutConfigStatus.Reserved, label: "Reserved" },
    { status: CheckoutConfigStatus.Expired, label: "Expired" },
    { status: "INTERNAL" as const, label: "Internal" },
    { status: "AUTHORIZED_USERS" as const, label: "Authorized Users" },
    // TODO: Add Salesforce opportunities handling when available
    // { status: "OPPORTUNITIES", label: "Opportunities" },
  ];

  const { isMobile } = useViewport();

  const navigate = useNavigate();
  const tid = useTestIds({}, "internalConfigurationsLandingPage");
  const { showModal, closeModal } = useModal();

  return (
    <>
      <div css={Css.df.asStretch.jcsb.aic.py2.px6.ifMobile.px1.$}>
        <img width="153" height="26" src="/logo.svg" alt="Homebound" />
        <div>
          <Button onClick={handleSignOut} size="small" variant={"secondary"}>
            Logout
            <Icon name="arrow-from-left" />
          </Button>
        </div>
      </div>
      <div css={Css.p6.df.fdc.gap1.ifMobile.px1.py3.$}>
        <div css={Css.df.fdc.gap5.$}>
          <div css={Css.df.jcsb.aic.$}>
            <h1 css={Css.t32.ifMobile.t24.$} {...tid.header}>
              Configurations
            </h1>
            <div>
              <Button
                btnFontSize="reg"
                size={isMobile ? "small" : "large"}
                rounded
                onClick={() => navigate(createCommunitiesUrl(), { state: { from: configurationsLandingPath } })}
                {...tid.addConfigurationButton}
              >
                + Configuration
              </Button>
            </div>
          </div>
          {/* Tabs */}
          <div css={Css.df.bb.bw2.bcTaupe.$}>
            {tabs.map((tab) => (
              <button
                key={tab.status}
                onClick={() => setActiveTab(tab.status)}
                {...tid[`activeTab_${defaultTestId(tab.status)}`]}
                css={Css.p2.t14.gray.bNone.if(activeTab === tab.status).gray800.boxShadow("0px 2px 0px 0px #353535").$}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div css={Css.df.gap2.aic.jcsb.$}>
            <SearchBar debounceDelayInMs={500} onSearch={setSearch} />
            {isAuthorizedUsersTab && (
              <div css={Css.fs0.$}>
                <Button
                  onClick={() => showModal(<AddAuthorizedUserModal onClose={closeModal} showToast={showToast} />)}
                  size="small"
                  rounded
                  variant={"secondary"}
                  outline={"black"}
                >
                  Add User
                </Button>
              </div>
            )}
          </div>
        </div>
        {isAuthorizedUsersTab ? (
          <AuthorizedUsersTable search={search} showToast={showToast} />
        ) : (
          <InternalConfigurationsLandingPageTable
            configurations={configurations}
            status={activeTab}
            sortState={checkoutConfigsSortState}
            toggleSort={toggleCheckoutConfigsSort}
          />
        )}
        {!isAuthorizedUsersTab && hasNextPage && maybeFetchMore && (
          <div css={Css.mt1.mb1.p2.ma.$}>
            <Button onClick={maybeFetchMore} size="medium">
              Load More
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

function ColumnHeader<K extends string>({
  label,
  sortKey,
  sortState,
  toggleSort,
}: {
  label: string;
  sortKey?: K;
  sortState?: SortState<K>;
  toggleSort?: (key: K) => void;
}) {
  const tid = useTestIds({}, `columnHeader${label}`);
  const [isHovered, setIsHovered] = useState(false);
  const isSortable = !!sortKey && !!toggleSort;
  const isActive = sortKey === sortState?.key;
  const direction = isActive ? sortState?.order : undefined;

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      css={{
        ...Css.p1.t10.midGray.mhPx(40).df.aic.gap1.if(isSortable).cursorPointer.usn.$,
        ...Css.if(isSortable).onHover.gray800.$,
      }}
      onClick={isSortable ? () => toggleSort(sortKey) : undefined}
      {...tid}
    >
      <span>{label}</span>
      {isSortable && (
        <span
          css={
            Css.fs0
              .visibility("hidden")
              .if(isActive || isHovered)
              .visibility("visible").$
          }
        >
          <Icon name={direction === Order.Desc ? "sortDown" : "sortUp"} size={16} />
        </span>
      )}
    </div>
  );
}

function TableCell({ children, isExpired }: { children: React.ReactNode; isExpired?: boolean }) {
  return <div css={{ ...Css.p1.t12.gray800.mhPx(40).df.aic.truncate.if(!!isExpired).red600.$ }}>{children}</div>;
}

type RowProps = {
  config: InternalConfigurationsLandingPage_CheckoutConfigFragment;
  showModal: (modal: React.ReactNode) => void;
  closeModal: () => void;
  showToast: (toast: ToastMessageProps) => void;
};

function Row(props: RowProps) {
  const { config, showModal, closeModal, showToast } = props;
  const { mask } = phoneMask();
  const tid = useTestIds({}, "configurationsRow");

  const [archiveConfig] = useInternalConfigurationsLandingPage_ArchiveCheckoutConfigMutation({
    refetchQueries: ["InternalConfigurationsLandingPage"],
  });

  const [unReserveConfig] = useInternalConfigurationsLandingPage_UnReserveCheckoutConfigMutation({
    refetchQueries: ["InternalConfigurationsLandingPage"],
  });

  const handleAction = (e: React.MouseEvent, action: "archive" | "unreserve") => {
    e.preventDefault();
    showModal(
      <Modal
        isOpen
        close={closeModal}
        size={{ width: "489px", height: "auto" }}
        title={
          <div css={Css.t24.$}>{action === "archive" ? "Archive This Configuration?" : "Unreserve Configuration?"}</div>
        }
        description={
          <div css={Css.t14.gray.fw400.$}>
            Are you sure you want to {action === "archive" ? "archive" : "unreserve"} "{config.name}"?
            <br />
            {action === "archive" ? "This action cannot be undone." : "The current reserved price will be lost."}
          </div>
        }
        actionBtns={[
          { variant: "link", children: "Cancel", onClick: closeModal },
          {
            children: action === "archive" ? "Archive" : "Unreserve",
            rounded: true,
            xss: Css.bgRed600.$,
            onClick: async () => {
              if (action === "archive") {
                await archiveConfig({ variables: { input: { id: config.id, archive: true } } });
                showToast({ variant: "success", title: "Archived", body: `"${config.name}" has been archived.` });
              } else {
                await unReserveConfig({ variables: { input: { id: config.id, preserveExpiresAt: false } } });
                showToast({ variant: "success", title: "Unreserved", body: `"${config.name}" has been unreserved.` });
              }
              closeModal();
            },
          },
        ]}
      />,
    );
  };

  return (
    <a
      css={{ ...TABLE_GRID_STYLES, ...Css.bb.bcGray200.cursorPointer.onHover.bgGray100.$ }}
      href={config.checkoutUrl?.path}
      {...tid}
    >
      <TableCell>
        <div css={Css.wsn.$}>{config.name}</div>
      </TableCell>
      <TableCell>
        <div css={Css.df.fdc.$}>
          <div css={Css.t12.gray800.$}>
            {config.customerFirstName} {config.customerLastName}
          </div>
          <div css={Css.t10.gray.$}>{config.customerEmail}</div>
          <div css={Css.t10.gray.$}>{mask(config.customerPhone ?? "")}</div>
        </div>
      </TableCell>
      <TableCell>
        <div css={Css.wsn.$}>{config.structure.name}</div>
      </TableCell>
      <TableCell>{config.community.name}</TableCell>
      {/* Note: our config createdAt and expiresAt values include TZ info so date-fns will automatically convert to local time */}
      <StatusDateColumn config={config} />
      <TableCell>{config.createdBy?.name ?? "N/A"}</TableCell>
      <TableCell>
        <div css={Css.df.gap1.aic.$}>
          {(config.status === CheckoutConfigStatus.Reserved || config.status === CheckoutConfigStatus.Expired) && (
            <button id="unreserve-btn" onClick={(e) => handleAction(e, "unreserve")} css={Css.df.aic.gap1.$}>
              <Icon name="lockOpen" />
              <TooltipV2 cssOverrides={Css.p1.$} anchorSelect={"#unreserve-btn"}>
                Unreserve
              </TooltipV2>
            </button>
          )}
          <button id="archive-btn" onClick={(e) => handleAction(e, "archive")} css={Css.df.aic.gap1.$}>
            <Icon name="archive" />
            <TooltipV2 cssOverrides={Css.p1.$} anchorSelect={"#archive-btn"}>
              Archive
            </TooltipV2>
          </button>
        </div>
      </TableCell>
    </a>
  );
}

function InternalConfigurationsLandingPageTable({
  configurations,
  status,
  sortState,
  toggleSort,
}: {
  configurations: InternalConfigurationsLandingPage_CheckoutConfigFragment[];
  status: LandingPageTabType;
  sortState: SortState<CheckoutConfigsSortKey>;
  toggleSort: (key: CheckoutConfigsSortKey) => void;
}) {
  const { showModal, closeModal } = useModal();
  const { showToast } = useToastMessage();

  // This is somewhat of an odd UX when it comes to sorting since this column gets swapped out based on the status
  // and if you are sorting by "reservedAt" then change to the "In Progress" table, you'll still be sorting by that value.
  // We could do something more fancy or treat each table flavor as its own sort state, but hoping to not add that complexity if we don't need it.
  const { dateLabel, dateSortKey } = useMemo<{ dateLabel: string; dateSortKey: CheckoutConfigsSortKey }>(() => {
    if (status === CheckoutConfigStatus.Expired) return { dateLabel: "Expired", dateSortKey: "expiresAt" };
    if (status === CheckoutConfigStatus.Reserved) return { dateLabel: "Reserved", dateSortKey: "reservedAt" };
    return { dateLabel: "Created", dateSortKey: "createdAt" };
  }, [status]);

  return (
    <div css={Css.df.fdc.bcGray300.oxa.ifMobileOrTablet.mwPx(MOBILE_TABLE_WIDTH).$}>
      {/* Table Header */}
      <div css={{ ...TABLE_GRID_STYLES, ...Css.bb.bcGray200.$ }}>
        <ColumnHeader label="Name" sortKey="name" sortState={sortState} toggleSort={toggleSort} />
        <ColumnHeader label="Client" sortKey="customerFirstName" sortState={sortState} toggleSort={toggleSort} />
        <ColumnHeader label="Address" sortKey="structureAddress" sortState={sortState} toggleSort={toggleSort} />
        <ColumnHeader label="Community" />
        <ColumnHeader label={dateLabel} sortKey={dateSortKey} sortState={sortState} toggleSort={toggleSort} />
        <ColumnHeader label="Created By" />
        <ColumnHeader label="" />
      </div>

      {/* Table Body */}
      {configurations.map((config) => (
        <Row key={config.id} config={config} showModal={showModal} closeModal={closeModal} showToast={showToast} />
      ))}
    </div>
  );
}

function StatusDateColumn({ config }: { config: InternalConfigurationsLandingPage_CheckoutConfigFragment }) {
  const { status, expiresAt, reservedAt, createdAt } = config;

  if (status === CheckoutConfigStatus.Expired && expiresAt) {
    return <TableCell isExpired>{dateFormatter(expiresAt, DATE_WITH_TIME_FORMAT)}</TableCell>;
  }

  if (status === CheckoutConfigStatus.Reserved && reservedAt) {
    return <TableCell>{dateFormatter(reservedAt, DATE_WITH_TIME_FORMAT)}</TableCell>;
  }

  return <TableCell>{dateFormatter(createdAt, DATE_WITH_TIME_FORMAT)}</TableCell>;
}

function AuthorizedUsersTable({
  search,
  showToast,
}: {
  search: string;
  showToast: (toast: ToastMessageProps) => void;
}) {
  const { sortState, toggleSort, order } = useSortState<HomeownersOrder>(
    "authorizedUsersList",
    "createdAt",
    Order.Desc,
  );
  // Only show authorized users created after October 1, 2025
  // This aligns with our checkout launch
  const filterByDate = new DateOnly(parseISO("2025-10-01"));
  const query = useInternalConfigurationsLandingPage_AuthorizedUsersQuery({
    variables: {
      filter: { createdAt: { op: DateOperation.After, value: filterByDate }, search },
      page: { limit: 50 },
      order,
    },
  });

  const authorizedUsers = query.data?.homeowners?.entities || [];
  const hasNextPage = query.data?.homeowners.pageInfo.hasNextPage ?? false;

  const fetchMore = async () => {
    if (hasNextPage) {
      await query.fetchMore({ variables: { page: { offset: authorizedUsers.length, limit: 50 } } });
    }
  };

  return (
    <>
      <div css={Css.df.fdc.bcGray300.oxa.ifMobileOrTablet.mwPx(MOBILE_TABLE_WIDTH).$}>
        {/* Table Header */}
        <div css={{ ...TABLE_GRID_STYLES, ...Css.bb.bcGray200.$ }}>
          <ColumnHeader label="Name" sortKey="firstName" sortState={sortState} toggleSort={toggleSort} />
          <ColumnHeader label="Email" />
          <ColumnHeader label="Phone" />
          <ColumnHeader label="Created" sortKey="createdAt" sortState={sortState} toggleSort={toggleSort} />
          <ColumnHeader label="Created By" />
          <ColumnHeader label="Checkout Access" />
        </div>

        {/* Table Body */}
        {authorizedUsers.map((user) => (
          <AuthorizedUserRow key={user.id} user={user} showToast={showToast} />
        ))}
      </div>
      {hasNextPage && (
        <div css={Css.mt1.mb1.p2.ma.$}>
          <Button onClick={fetchMore} size="medium">
            Load More
          </Button>
        </div>
      )}
    </>
  );
}

function AuthorizedUserRow({
  user,
  showToast,
}: {
  user: InternalConfigurationsLandingPage_HomeownerFragment;
  showToast: (toast: ToastMessageProps) => void;
}) {
  const { mask } = phoneMask();
  const tid = useTestIds({}, "authorizedUserRow");
  const [saveHomeowner] = useInternalConfigurationsLandingPage_SaveHomeownerMutation();

  const handleCheckboxClick = async () => {
    try {
      await saveHomeowner({
        variables: {
          input: {
            id: user.id,
            canViewCheckout: !(user.canViewCheckout ?? false),
          },
        },
      });
    } catch {
      showToast({
        variant: "error",
        title: "Update Failed",
        body: `Failed to update checkout access for ${user.firstName} ${user.lastName}. Please try again.`,
      });
    }
  };

  return (
    <div css={{ ...TABLE_GRID_STYLES, ...Css.bb.bcGray200.$ }} {...tid}>
      <TableCell>
        <div css={Css.df.fdc.$}>
          <div css={Css.t12.gray800.$}>
            {user.firstName} {user.lastName}
          </div>
        </div>
      </TableCell>
      <TableCell>{user.email}</TableCell>
      <TableCell>{mask(user.phone ?? "")}</TableCell>
      <TableCell>{dateFormatter(user.createdAt, DATE_WITH_TIME_FORMAT)}</TableCell>
      <TableCell>{user.createdBy?.name ?? "N/A"}</TableCell>
      <TableCell>
        <Checkbox onClick={handleCheckboxClick} selected={!!user.canViewCheckout} />
      </TableCell>
    </div>
  );
}
